import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/hooks/use-toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface Category {
  id: string;
  name: string;
  category_group_id: string | null;
}

interface Quote {
  id: string;
  content: string;
}

const fallbackCategories = [
  { id: '1', name: 'Inspiration', category_group_id: null },
  { id: '2', name: 'Motivation', category_group_id: null },
  { id: '3', name: 'Success', category_group_id: null },
  { id: '4', name: 'Leadership', category_group_id: null },
  { id: '5', name: 'Wisdom', category_group_id: null },
  { id: '6', name: 'Happiness', category_group_id: null },
  { id: '7', name: 'Life', category_group_id: null },
  { id: '8', name: 'Love', category_group_id: null },
];

export function CategoriesShowcase() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryQuotes, setCategoryQuotes] = useState<Record<string, Quote>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [usedFallback, setUsedFallback] = useState(false);

  const fetchCategoryQuote = async (categoryId: string) => {
    try {
      const { data: quoteCategories, error: quoteCatError } = await supabase
        .from('quote_categories')
        .select('quote_id')
        .eq('category_id', categoryId)
        .limit(1);

      if (quoteCatError) throw quoteCatError;
      
      if (quoteCategories && quoteCategories.length > 0) {
        const { data: quotes, error: quotesError } = await supabase
          .from('quotes')
          .select('id, content')
          .eq('id', quoteCategories[0].quote_id)
          .single();

        if (quotesError) throw quotesError;
        if (quotes) {
          setCategoryQuotes(prev => ({
            ...prev,
            [categoryId]: quotes
          }));
        }
      }
    } catch (err) {
    }
  };

  useEffect(() => {
    if (categories.length > 0) {
      categories.forEach(category => {
        fetchCategoryQuote(category.id);
      });
    }
  }, [categories]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .order('name', { ascending: true });

        if (error) throw error;
        
        if (data && data.length > 0) {
          setCategories(data);
        } else {
          setCategories(fallbackCategories);
          setUsedFallback(true);
        }
      } catch (err: any) {
        setError('Failed to load categories. Please try again.');
        
        if (retryCount >= 3) {
          setCategories(fallbackCategories);
          setUsedFallback(true);
        } else if (retryCount < 3) {
          const delay = 2000 * (retryCount + 1);
          toast({
            title: "Loading categories failed",
            description: `Retrying in ${delay / 1000} seconds...`,
            variant: "destructive"
          });
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, delay);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [retryCount]);

  useEffect(() => {
    if (!loading && categories.length === 0 && retryCount === 0) {
      setRetryCount(1);
    }
  }, [loading, categories, retryCount]);

  if (loading) {
    return <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-pulse">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="h-10 bg-gray-200 rounded-md"></div>
      ))}
    </div>;
  }

  if (error && !usedFallback) {
    return <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>{error}</AlertDescription>
      <button onClick={() => setRetryCount(prev => prev + 1)} className="mt-2 text-sm underline">
        Retry
      </button>
    </Alert>;
  }

  return <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {categories.map(category => (
      <TooltipProvider key={category.id}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4 h-10 flex items-center justify-center">
                <h3 className="text-center font-medium">{category.name}</h3>
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent>
            {categoryQuotes[category.id] ? (
              <p className="max-w-xs">{categoryQuotes[category.id].content}</p>
            ) : (
              <div className="flex items-center space-x-2">
                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                <p>Loading quote...</p>
              </div>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ))}
    
    {categories.length === 0 && (
      <div className="col-span-full text-center py-8 text-muted-foreground">
        <p>No categories available. Check back soon!</p>
      </div>
    )}
    
    {usedFallback && (
      <div className="col-span-full text-center pt-2 text-xs text-muted-foreground">
        <p>Displaying popular categories</p>
      </div>
    )}
  </div>;
}
